import { sprintf } from 'sprintf-js';
import styled from 'styled-components';

import { Box } from 'components/Box/Box';
import { Button } from 'components/Button/Button';
import { LegacyTertiaryButton } from 'components/LegacyButton/LegacyTertiaryButton';
import { Tooltip } from 'components/Tooltip/Tooltip';
import { useSaveListing } from 'hooks/useSaveListing';
import { SearchType } from 'modules/search/types/SearchType';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { trackEventOnce } from 'utils/analytics/track/trackEventOnce';
import { LISTING_TYPE_PLURAL_HUMAN_NAME_MAP } from 'utils/constants/general/listingTypePluralHumanNameMap';
import { OrgType } from 'utils/constants/general/orgTypes';

type Props = {
  disabled?: boolean;
  listingId: string;
  listingName: string;
  listingType: SearchType;
  orgId?: string;
  orgName?: string;
  orgType?: OrgType;
  tabIndex?: number;
};

const ButtonContainer = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
`;

export function SaveButton({
  disabled,
  listingId,
  listingName,
  listingType,
  orgId,
  orgName,
  orgType,
  tabIndex,
}: Props) {
  const {
    saved,
    saveOrg,
    setShowOrgModal,
    showOrgModal,
    canSave,
    saveListing,
    unsaveListing,
  } = useSaveListing({
    listingId,
    listingType,
    listingName,
    orgId,
    orgName,
    orgType,
  });

  if (!canSave) return null;

  const buttonText = saved ? getText('Unsave') : getText('Save');

  const onConfirm = async () => {
    trackClicked('Save Org Tooltip', { cta_type: 'yes' });
    await saveOrg();
    setShowOrgModal(false);
  };

  const onDecline = async () => {
    trackClicked('Save Org Tooltip', { cta_type: 'no' });
    setShowOrgModal(false);
  };

  return (
    <Tooltip
      content={
        <div data-qa-id="save-listing-tooltip">
          <Box mb={3}>
            {sprintf(
              getText(
                'Would you like to get alerts when this organization posts new %(listingTypeName)s?',
              ),
              {
                listingTypeName:
                  LISTING_TYPE_PLURAL_HUMAN_NAME_MAP[listingType],
              },
            )}
          </Box>

          <ButtonContainer>
            <LegacyTertiaryButton
              onClick={onDecline}
              data-qa-id="decline-button"
            >
              {getText('No, thank you')}
            </LegacyTertiaryButton>
            <Button
              type="button"
              variant="primary"
              onClick={onConfirm}
              disabled={false}
              data-qa-id="confirm-button"
            >
              {getText('Yes!')}
            </Button>
          </ButtonContainer>
        </div>
      }
      placement="bottom-left"
      closeable
      size="large"
      variant="brand-blue"
      renderCondition={showOrgModal}
      onRender={() => trackEventOnce('Displayed: Save Org Tooltip')}
    >
      <div>
        <Button
          type="button"
          disabled={disabled}
          aria-label={buttonText}
          variant="secondary"
          icon={{ type: 'normalized', name: 'bookmark', size: 16 }}
          data-qa-id={saved ? 'unsave-listing' : 'save-listing'}
          onClick={saved ? unsaveListing : saveListing}
          tabIndex={tabIndex}
          title={buttonText}
        >
          {buttonText}
        </Button>
      </div>
    </Tooltip>
  );
}
